/*
 * Waiver template SEAM — canon §6 (`waiver.flow.*`) + boardroom ruling E
 * (BOARDROOM-2026-08-09-133534): the waiver Documents → Sections → Clauses
 * DATA is organiser-authored and OWNED BY settings-laptop. Settings has now
 * SURFACED the authoring area (settings PR#215, SETTINGS-2026-08-11-083837),
 * so this module wires the DISPLAY half: it fetches the organiser-authored
 * resolved template from settings' resolved endpoint (SAME pattern as
 * src/data/participantConfig.ts) and maps it into the typed WaiverTemplate the
 * flow consumes unchanged. When settings has NOT published a waiver for an
 * event (404 / empty document set), it resolves null and the flow renders its
 * honest "not available yet" empty state (ruling A).
 *
 * NOTHING here fabricates documents (canon §6 must-not: waiver documents /
 * sections / clauses must NOT be hardcoded; ruling A,
 * PROGRAMME-AD-NO-FABRICATED-DATA-IN-BUILDS-V1 LOCKED) — every literal that
 * renders comes off the wire from settings. The canon [demo] document set
 * lives in src/screens/waiver/waiverTemplate.fixture.ts — TEST FIXTURE ONLY,
 * never imported by shipped code.
 *
 * RESOLVER CONTRACT ASSUMED (settings must CONFIRM — routed via boardroom):
 *   GET {VITE_SETTINGS_API_BASE}/api/events/:eventId/waiver-template/resolved
 *   Headers: Authorization: Bearer {cognito idToken}
 *   200 → ResolvedWaiverTemplateResponse (shape below — a 1:1 mirror of
 *         WaiverTemplate: organiser/event + documents→sections→clauses).
 *   404 (or 200 with a null/empty documents array) → no published waiver ⇒
 *         null (honest empty state).
 *   other non-2xx → thrown (the flow catches → honest empty, never a mock).
 * This mirrors participant-config's `/participant-config/resolved` verb,
 * bearer auth and per-event keying. Guardian-variant resolution (canon §6:
 * "arrives already resolved" per signer context) is NOT keyed here — guardian
 * entry is unwired this cycle (waiverModel §12.2) so the resolver is keyed by
 * eventId only for the self signer.
 */
import { loadSession } from '../auth/session'
import { waiverSnapshotReceiptEnabled } from '../lib/waiverSnapshotFlag'
import { myApiBase } from './myApi'

export interface WaiverClause {
  /** 1.2.1-style clause number (canon §6 numbering). */
  num: string
  text: string
  /** Sub-clause (1.2.x) — indents visibly deeper than top-level clauses. */
  sub?: boolean
}

export interface WaiverSection {
  name: string
  /** Organiser's choice — drives the MUST ACCEPT / OPTIONAL tag and gating
   *  (canon §6: one tick per SECTION; required flag is the organiser's). */
  required: boolean
  /** Consent-row text, organiser-authored. Guardian variants ("on {who}'s
   *  behalf") arrive already resolved in the template — not composed here. */
  consent: string
  /** Optional note under the clauses (e.g. the photography / optional notes). */
  note?: string
  clauses: WaiverClause[]
}

export interface WaiverDocument {
  /** Stable id → DOM id `waiver.doc.{docId}` (canon §1.3: guardian|waiver|rules). */
  id: string
  title: string
  /** Display version, e.g. `v4.2` (canon §6 — versions are part of the record). */
  version: string
  /** Optional per-doc intro override (e.g. race rules: `A separate document —
   *  the organiser may update these before race day.`). */
  intro?: string
  sections: WaiverSection[]
}

export interface WaiverTemplate {
  /** Organiser display name — the `{org}` slot in the canon chrome templates. */
  organiser: string
  /** Event display name — the `{event}` slot. */
  event: string
  /** Ordered doc set AS RESOLVED by settings for this signer context (the
   *  guardian variant arrives with the Guardian declaration first — canon §6). */
  documents: WaiverDocument[]
  /** OPTIONAL event banner for the signed-waiver PRINT view — settings'
   *  org_unit `banner_desktop_url` carried on the resolved payload (the durable
   *  fix named in WaiverFlow's interim EVENT_BANNER_URL hardcode, 8d87c21 /
   *  BOARDROOM-134838). Settings does NOT emit the field yet — read tolerantly
   *  (absent today ⇒ the print banner cascade falls to org-branding hero →
   *  legacy map → wordmark). The key is only SET when the wire carries a
   *  non-empty string — and it is PRESENTATION-ONLY, permanently excluded from
   *  the legal signed-record hash (canonicalTemplateText projects organiser/
   *  event/documents only), so settings starting to emit it can never
   *  invalidate an already-signed template's hash. */
  bannerDesktopUrl?: string
  /** settings' top-level guardian.declaration_markdown (trimmed) — the
   *  parent/guardian declaration (boardroom ruling 4 on DESIGN-MY-WAIVER-
   *  SIGNING-SNAPSHOT-V1: rendered on the sign page AND the signed copy).
   *  Mapped ONLY with VITE_WAIVER_SNAPSHOT_RECEIPT_ENABLED on (the mapper
   *  option), and only SET when non-empty, so with the flag off every
   *  template maps and hashes exactly as before. LEGALLY MATERIAL: when set
   *  it is part of canonicalTemplateText (the signed pin). my-api's port
   *  (services/my-api/src/waiver-snapshot.ts) mirrors this byte for byte —
   *  the shared golden fixture pins both. */
  guardianDeclaration?: string
}

// ---------------------------------------------------------------------------
// Settings resolver (mirrors src/data/participantConfig.ts)
// ---------------------------------------------------------------------------

/** Wire shape assumed for settings' resolved waiver-template endpoint — a 1:1
 *  mirror of WaiverTemplate. Every field is validated/coerced in the mapper so
 *  a malformed payload degrades to the honest empty state, never a crash. */
interface ResolvedWaiverTemplateResponse {
  organiser?: unknown
  event?: unknown
  /** Template-wide version — settings versions the whole published template via
   *  content_version (documents carry no per-doc version). */
  content_version?: unknown
  documents?: unknown
  /** Event banner for the signed-waiver print (org_unit banner_desktop_url).
   *  NOT emitted by settings yet — optional/tolerant by design; snake_case is
   *  settings' column name, camelCase accepted in case it ships mapped. */
  banner_desktop_url?: unknown
  bannerDesktopUrl?: unknown
  /** settings guardian block: { required_when_minor, declaration_markdown }. */
  guardian?: unknown
}

/** Settings API base — read from env only (nothing invented); honest null when
 *  unset. Same env var + posture as participantConfigApiBase. */
export function waiverTemplateApiBase(
  env: Record<string, string | undefined> = import.meta.env,
): string | null {
  return env.VITE_SETTINGS_API_BASE || null
}

function asString(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

/** Derive the organiser/event name from a document title's prefix — settings
 *  authors titles as "{Org} — {DocName}" (em-dash, en-dash, or hyphen), e.g.
 *  "OnePulse — Participant & Parent/Guardian Waiver and Release" → "OnePulse".
 *  Used only as a fallback when the resolved payload carries no organiser field
 *  (quality 6c: the counterparty rendered blank). Returns '' if no separator. */
export function orgFromDocTitle(title: unknown): string {
  const t = asString(title).trim()
  const m = t.split(/\s[—–-]\s/)
  return m.length > 1 ? m[0]!.trim() : ''
}

/** true ⇔ the template names a counterparty (the organiser the participant
 *  contracts with). A waiver without one must never be signable. */
export function hasCounterparty(template: Pick<WaiverTemplate, 'organiser'>): boolean {
  return typeof template.organiser === 'string' && template.organiser.trim() !== ''
}

// The mapper accepts BOTH the settings resolver's ACTUAL field names (captured
// live 2026-08-13 for the OnePulse event, BOARDROOM-2026-08-12-221057) and the
// originally-assumed names, so a valid published template renders instead of the
// empty state. Real → assumed: document_id→id, section.title→name,
// acknowledgement_required→required, clause.body_markdown→text, clause.order→num,
// top-level content_version→doc version. Every field still validated/coerced so a
// malformed payload degrades gracefully, never crashes (ruling A).
function mapClause(raw: unknown): WaiverClause | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const text = asString(r.text) || asString(r.body_markdown)
  // Number: explicit `num`, else the settings clause `order`, else the stable
  // `clause_id`. A clause with text but no number/id is unnumbered → drop.
  const num =
    asString(r.num) ||
    (typeof r.order === 'number' ? String(r.order) : '') ||
    asString(r.clause_id)
  if (!num || !text) return null
  const clause: WaiverClause = { num, text }
  if (r.sub === true) clause.sub = true
  return clause
}

function mapSection(raw: unknown): WaiverSection | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const name = asString(r.name) || asString(r.title)
  // Name is the section heading — without it there is nothing to render → drop.
  if (!name) return null
  const clauses = Array.isArray(r.clauses)
    ? r.clauses.map(mapClause).filter((c): c is WaiverClause => c !== null)
    : []
  // Consent is the per-section tick-row label — organiser-authored. When the
  // template omits it (the typed-signature acceptance model: every section
  // acknowledgement_required=false, acceptance is the signature, no per-section
  // tick), consent is '' and the flow shows no tick for that read-only section.
  const consent = asString(r.consent)
  const required = r.required === true || r.acknowledgement_required === true
  // Renderable when it has a heading AND something to show — clauses to read OR a
  // consent row to tick. (Was `name && consent`, which dropped EVERY section of a
  // read-then-sign template → the whole document → the false empty state.)
  if (clauses.length === 0 && !consent) return null
  const section: WaiverSection = { name, required, consent, clauses }
  const note = asString(r.note)
  if (note) section.note = note
  return section
}

function mapDocument(raw: unknown, defaultVersion: string): WaiverDocument | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const id = asString(r.id) || asString(r.document_id)
  const title = asString(r.title)
  if (!id || !title) return null
  const sections = Array.isArray(r.sections)
    ? r.sections.map(mapSection).filter((s): s is WaiverSection => s !== null)
    : []
  // A document with no acceptable sections has nothing to sign — drop it.
  if (sections.length === 0) return null
  // Per-doc version if present, else the template-wide content_version.
  const version = asString(r.version) || defaultVersion
  const doc: WaiverDocument = { id, title, version, sections }
  const intro = asString(r.intro)
  if (intro) doc.intro = intro
  return doc
}

/**
 * Map the resolved wire shape into this app's WaiverTemplate contract. Pure —
 * unit-tested. Defensive like participantConfig's mapper: malformed/empty
 * clauses, sections and documents are dropped rather than crashing the flow.
 * A payload that resolves to ZERO documents (nothing published, or all
 * malformed) returns null → the flow's honest empty state (never a fabricated
 * document — ruling A).
 */
export function mapResolvedWaiverTemplate(
  json: ResolvedWaiverTemplateResponse | null,
  opts: { includeGuardianDeclaration: boolean } = { includeGuardianDeclaration: false },
): WaiverTemplate | null {
  if (!json || typeof json !== 'object') return null
  // Template-wide version fallback for documents that carry no per-doc version.
  const cv = json.content_version
  const defaultVersion = typeof cv === 'number' ? `v${cv}` : asString(cv)
  const documents = Array.isArray(json.documents)
    ? json.documents
        .map((d) => mapDocument(d, defaultVersion))
        .filter((d): d is WaiverDocument => d !== null)
    : []
  if (documents.length === 0) return null
  // CMT-2026-08-15 (quality 6c): the resolved payload substitutes the organiser
  // name INTO the clause text + doc titles but carries NO top-level organiser/
  // event field — so `json.organiser` was empty and the flow rendered
  // "Between you and ." (blank counterparty). Derive the organiser/event from the
  // first document's title prefix (settings authors titles as "{Org} — {DocName}",
  // e.g. "OnePulse — Participant & …Waiver…") when the field is absent, so the
  // counterparty renders. Settings is adding an explicit top-level `organiser`
  // (legal name when set, else the unit name — boardroom 2026-10-01); it wins.
  // Both empty ⇒ organiser '' and the flow withholds signing (hasCounterparty)
  // rather than render "Between you and .".
  const derivedOrg = orgFromDocTitle(documents[0]?.title)
  const template: WaiverTemplate = {
    organiser: asString(json.organiser).trim() || derivedOrg,
    event: asString(json.event).trim() || derivedOrg,
    documents,
  }
  // GUARDIAN DECLARATION (flag on only) — inserted right after documents so
  // the canonical text's key order is fixed (organiser, event, documents,
  // guardianDeclaration). Never set when empty.
  if (opts.includeGuardianDeclaration) {
    const g = json.guardian && typeof json.guardian === 'object' ? (json.guardian as { declaration_markdown?: unknown }) : null
    const decl = asString(g?.declaration_markdown).trim()
    if (decl) template.guardianDeclaration = decl
  }
  // OPTIONAL print-banner field (banner cascade tier 1) — settings does not
  // emit it yet; only SET when a non-empty string arrives so today's payloads
  // map (and hash) exactly as before. Trimmed; never fabricated.
  const banner = (asString(json.banner_desktop_url) || asString(json.bannerDesktopUrl)).trim()
  if (banner) template.bannerDesktopUrl = banner
  return template
}

/**
 * Fetch + map the resolved waiver template for an event from settings. 404
 * means "no waiver published for this event" — an expected, honest state that
 * resolves null (the flow shows its empty state). Any other non-2xx throws so
 * a transient failure is surfaced, never silently treated as "no waiver". Args
 * mirror fetchParticipantConfigDataCollection for testability.
 */
export async function fetchWaiverTemplateFrom(
  eventId: string,
  base: string,
  idToken: string,
  fetchImpl: typeof fetch = fetch,
): Promise<WaiverTemplate | null> {
  const url =
    `${base.replace(/\/$/, '')}/api/events/${encodeURIComponent(eventId)}/waiver-template/resolved`
  const res = await fetchImpl(url, { headers: { Authorization: `Bearer ${idToken}` } })
  if (res.status === 404) return null
  if (!res.ok) throw new Error(`waiver-template resolved fetch failed (${res.status})`)
  return mapResolvedWaiverTemplate((await res.json()) as ResolvedWaiverTemplateResponse, {
    includeGuardianDeclaration: waiverSnapshotReceiptEnabled(),
  })
}

/**
 * Resolve the organiser-authored waiver template for an event — the seam the
 * waiver flow consumes. Wires the env-configured settings base + the signed-in
 * user's idToken, then delegates to fetchWaiverTemplateFrom. Honest null when
 * settings is unconfigured (no base) or the user has no session (no token) —
 * nothing to sign is shown rather than an invented document (ruling A).
 */
export async function fetchWaiverTemplate(eventId: string): Promise<WaiverTemplate | null> {
  const session = loadSession()
  if (!session) return null
  // CMT-2026-08-14-038 (re-open): PREFER the my-api BFF proxy. The browser
  // cannot fetch settings' /api/.../waiver-template/resolved directly — that
  // cross-origin call is CORS-blocked (settings sends no Access-Control-Allow-
  // Origin for the my.trumin origin), so the waiver never loaded. my-api is
  // already CORS-allowed for this origin and proxies to settings server-side.
  // Fall back to settings-direct only when the BFF route isn't deployed/wired
  // (older my-api or SETTINGS_API_BASE unset) so we degrade safely pre-deploy.
  const viaBff = await tryWaiverTemplateViaBff(eventId, session.idToken)
  if (viaBff.handled) return viaBff.template
  const base = waiverTemplateApiBase()
  if (!base) return null
  return fetchWaiverTemplateFrom(eventId, base, session.idToken)
}

/**
 * Attempt the my-api BFF proxy. Returns `{handled:true}` with the mapped
 * template (or null for an authoritative "no waiver published") when the BFF
 * gave a definitive answer; `{handled:false}` when the route is absent / not
 * wired (404-not-this-route or 503) so the caller falls back to settings-direct.
 * A genuine upstream failure (502/network with the route present) THROWS so the
 * flow shows its distinct load-error state, never a false "unpublished".
 */
export async function tryWaiverTemplateViaBff(
  eventId: string,
  idToken: string,
  fetchImpl: typeof fetch = fetch,
  base: string | null = myApiBase(),
): Promise<{ handled: true; template: WaiverTemplate | null } | { handled: false }> {
  if (!base) return { handled: false }
  const url = `${base.replace(/\/$/, '')}/me/events/${encodeURIComponent(eventId)}/waiver-template/resolved`
  let res: Response
  try {
    res = await fetchImpl(url, { headers: { Authorization: `Bearer ${idToken}` } })
  } catch {
    return { handled: false } // my-api unreachable → try settings-direct
  }
  if (res.status === 404) {
    // Disambiguate settings' authoritative "no waiver" from an older my-api that
    // simply doesn't have this route: the BFF stamps error='no_waiver_published'.
    const body = (await res.json().catch(() => null)) as { error?: string } | null
    if (body?.error === 'no_waiver_published') return { handled: true, template: null }
    return { handled: false } // route not deployed → fall back
  }
  if (res.status === 503) return { handled: false } // settings base unset on my-api → fall back
  if (!res.ok) throw new Error(`waiver BFF resolve failed (${res.status})`)
  return {
    handled: true,
    template: mapResolvedWaiverTemplate((await res.json()) as ResolvedWaiverTemplateResponse, {
      includeGuardianDeclaration: waiverSnapshotReceiptEnabled(),
    }),
  }
}

/**
 * Canonical serialisation of a template for hashing into the signed record.
 *
 * PROJECTS the legally-material fields ONLY — organiser, event, documents
 * (PR#119 adversarial-review P2). The old whole-struct JSON.stringify pulled
 * EVERY WaiverTemplate key into the legal hash, so any future optional
 * PRESENTATION field (bannerDesktopUrl — a mutable artwork URL settings may
 * start emitting) would flip the hash of an already-signed template on
 * re-resolve. The projection decouples presentation from the signature
 * permanently, robust to ANY future optional field.
 *
 * BYTE-COMPAT: for every payload settings emits today the projection is
 * BYTE-IDENTICAL to the old whole-struct serialisation — the mapper builds the
 * struct as exactly `{ organiser, event, documents }` in that insertion order
 * and only adds bannerDesktopUrl when the wire carries it (never today), so
 * existing signed-record hashes are untouched. Pinned by test
 * ("byte-compat proof" in waiverTemplate.test.ts) — do not reorder these keys.
 */
export function canonicalTemplateText(template: WaiverTemplate): string {
  // The guardian declaration is legally material: when the template carries
  // it (flag-on mapping only) it joins the pin, after documents. Absent ⇒ the
  // pre-existing three-key text, byte for byte.
  if (template.guardianDeclaration) {
    return JSON.stringify({
      organiser: template.organiser,
      event: template.event,
      documents: template.documents,
      guardianDeclaration: template.guardianDeclaration,
    })
  }
  return JSON.stringify({
    organiser: template.organiser,
    event: template.event,
    documents: template.documents,
  })
}

/** Deterministic FNV-1a hash so a signature record pins exactly which
 *  template was on screen at signing time (carried into my-api's waiver
 *  record — see WaiverInput.templateHash). Pure + sync. */
export function hashWaiverTemplate(text: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return (h >>> 0).toString(16).padStart(8, '0')
}
